// Smooth scrolling for every in-page anchor (bug 1036).
//
// Anchors on this site are authored in rich text: TinyMCE's anchor plugin writes
// `<a id="name"></a>`, and editors also give headings an id by hand. Two kinds of link point at
// them, and they used to behave differently on the same page:
//
//   - A rich-text link the editor remembered to tag `class="smooth-scroll"` animated, via the old
//     jQuery module this one replaces.
//   - A CMS link button (hero and CTA blocks) jumped. It carries no such class, and the link
//     picker cannot store a bare "#anchor" - it stores the page plus a remaining url - so its href
//     is a full path with the fragment on the end. The old module resolved targets with `$(href)`,
//     treating the href as a selector, so that form could never have worked even with the class.
//
// Rather than have editors opt in per link, this handles every anchor on the page from one
// delegated listener: whatever produced the link, a fragment pointing at this page scrolls.
//
// The header is `position: fixed`, so its measured height is subtracted from the target's offset;
// scrolling the target to 0 would put it behind the header. It is measured at click time rather
// than hardcoded because it changes with the breakpoint and grows when a notification bar shows.

// Breathing room between the bottom of the header and the top of the target.
const SCROLL_GAP = 16;

// Links whose href is a selector for a widget to open, not a place to go. Bootstrap's collapse and
// tab plugins read the href themselves (the FAQ accordion is `<a data-toggle="collapse"
// href="#Collapse123">`), so scrolling on those clicks would fight the widget.
//
// `role="button"` is deliberately NOT in this list. It reads like a widget signal but is not one
// here: _CTABlock.cshtml puts it on every CTA link button, and the rich-text editor puts it on the
// styled link buttons editors insert - which is precisely the link in bug 1036. The icon controls
// that carry it (header search, menu toggle) have `href="#"`, so the empty-fragment check already
// leaves them alone.
const UI_CONTROL_SELECTOR = [
    '[data-toggle]',
    '[data-bs-toggle]',
    '[data-slide]',
    '[data-slide-to]',
    '[role="tab"]',
].join(',');

class AnchorScroll {

    constructor() {
        // One delegated listener rather than a binding per link: anchors also arrive with markup
        // rendered after load (Vue components, the course calendar), and those would be missed by
        // a querySelectorAll at startup.
        document.addEventListener('click', event => this.onClick(event));
    }

    onClick(event) {
        // Something else already handled this click (a widget, another module).
        if (event.defaultPrevented) {
            return;
        }

        // Anything but a plain left click is the visitor asking for a new tab or window, or a
        // context menu. Leave all of it to the browser.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        const link = this.closestLink(event.target);

        if (!link || link.closest(UI_CONTROL_SELECTOR) || link.hasAttribute('download')) {
            return;
        }

        // An explicit target means a different browsing context; the fragment is not ours to act on.
        if (link.target && link.target !== '_self') {
            return;
        }

        if (!this.isSamePage(link)) {
            return;
        }

        const target = this.resolveTarget(link);

        if (!target) {
            // A fragment whose target is gone - an anchor deleted after the link was written.
            // The browser's own behaviour (do nothing much) is the honest outcome.
            return;
        }

        event.preventDefault();

        this.scrollTo(target);

        // Keep the fragment in the URL so the link stays shareable. replaceState rather than
        // pushState: jumping within a page is not navigation, and filling the back button with
        // anchors would trap the visitor on the page.
        if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', link.hash);
        }

        this.focusTarget(target);
    }

    /** The anchor the click landed in, tolerating clicks on an icon or span inside it. */
    closestLink(node) {
        // `closest` is missing on text nodes, and on SVG elements in a few older browsers - the
        // glyph inside a link is an inline SVG, so step up to a parent that has it.
        while (node && typeof node.closest !== 'function') {
            node = node.parentNode;
        }

        return node ? node.closest('a[href]') : null;
    }

    /**
     * Whether the link points at a fragment of the page we are already on.
     *
     * Trailing slashes are normalised on both sides: the CMS link picker renders the page url with
     * a trailing slash ("/dk/.../mep/#features") while the visitor may well have arrived on the
     * same page without one, and a strict comparison would then treat an in-page jump as a
     * navigation to somewhere else.
     */
    isSamePage(link) {
        if (!link.hash || link.hash === '#') {
            return false;
        }

        if (link.search !== window.location.search) {
            return false;
        }

        return this.normalisePath(link.pathname) === this.normalisePath(window.location.pathname);
    }

    normalisePath(path) {
        return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
    }

    /** The element a link's fragment points at, or null when nothing on the page matches. */
    resolveTarget(link) {
        // The fragment is percent-encoded in the href but not in the id, and an id can contain
        // characters that are not legal in a selector, so look it up by id rather than building a
        // selector out of it.
        let name = link.hash.slice(1);

        try {
            name = decodeURIComponent(name);
        } catch (e) {
            // A malformed escape sequence - fall back to the raw fragment.
        }

        const byId = document.getElementById(name);

        if (byId) {
            return byId;
        }

        // Older content (and the legacy solution) wrote `<a name="...">` instead of an id.
        const named = document.getElementsByName(name);

        return named.length ? named[0] : null;
    }

    scrollTo(target) {
        const top = Math.max(
            0,
            window.pageYOffset + target.getBoundingClientRect().top - this.headerOffset() - SCROLL_GAP,
        );

        // A visitor who has asked for reduced motion gets the jump they asked for.
        if (this.prefersReducedMotion()) {
            window.scrollTo(0, top);
            return;
        }

        try {
            window.scrollTo({ top, behavior: 'smooth' });
        } catch (e) {
            // Older browsers only accept scrollTo(x, y).
            window.scrollTo(0, top);
        }
    }

    /** The fixed header's height, measured rather than hardcoded. Zero when it is not fixed. */
    headerOffset() {
        const header = document.querySelector('.header');

        if (!header) {
            return 0;
        }

        return window.getComputedStyle(header).position === 'fixed'
            ? header.getBoundingClientRect().height
            : 0;
    }

    /**
     * Moves focus to the target so that whatever the visitor reads or tabs to next is inside the
     * section they asked for, rather than back at the top of the page.
     *
     * Anchors written by TinyMCE are empty inline elements with no natural focus, so give the
     * target a temporary tabindex of -1: focusable programmatically, never a tab stop. It is
     * dropped again on blur so it does not become a permanent one.
     */
    focusTarget(target) {
        const hadTabIndex = target.hasAttribute('tabindex');

        if (!hadTabIndex) {
            target.setAttribute('tabindex', '-1');
        }

        // preventScroll, or the browser's own focus scroll would undo the header offset.
        target.focus({ preventScroll: true });

        if (!hadTabIndex) {
            target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
        }
    }

    prefersReducedMotion() {
        return typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
}

export default AnchorScroll;
